import { useCallback, useEffect, useRef, useState } from 'react'

export type CopyState = 'idle' | 'copied' | 'failed'

/** Tempo até a limpeza automática da área de transferência. */
export const CLIPBOARD_TTL_MS = 30_000

/**
 * Cópia com autodestruição.
 *
 * Deixar uma senha parada na área de transferência é um vazamento silencioso:
 * qualquer app, site com permissão de colar ou histórico de clipboard do
 * sistema pode lê-la depois. Sobrescrevemos com string vazia após 30 s.
 *
 * É melhor-esforço: alguns navegadores só permitem escrever no clipboard
 * durante um gesto do usuário e podem recusar a limpeza. Por isso o aviso na
 * interface diz o que tentamos fazer, sem prometer garantia.
 */
export function useClipboard(options: { autoClear?: boolean } = {}) {
  const { autoClear = false } = options
  const [state, setState] = useState<CopyState>('idle')
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastCopied = useRef<string | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
      if (clearTimer.current) clearTimeout(clearTimer.current)
    },
    [],
  )

  const copy = useCallback(
    async (text: string) => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
      if (clearTimer.current) clearTimeout(clearTimer.current)

      try {
        await navigator.clipboard.writeText(text)
        lastCopied.current = text
        setState('copied')
      } catch {
        setState('failed')
        return
      }

      resetTimer.current = setTimeout(() => setState('idle'), 2200)

      if (autoClear) {
        clearTimer.current = setTimeout(() => {
          // Só apaga se ainda for o nosso conteúdo — não atrapalha algo que o
          // usuário tenha copiado depois.
          navigator.clipboard
            .readText()
            .then((current) => {
              if (current === lastCopied.current) return navigator.clipboard.writeText('')
              return undefined
            })
            .catch(() => {
              // Sem permissão de leitura (o caso comum): sobrescreve às cegas.
              return navigator.clipboard.writeText('').catch(() => undefined)
            })
          lastCopied.current = null
        }, CLIPBOARD_TTL_MS)
      }
    },
    [autoClear],
  )

  return { state, copy }
}
